import React from 'react';
import classNames from 'classnames';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { Spinner } from 'app/src/components/Spinner';
import { forTemplate } from 'app/common/lib/util/i18n';
import {
  ExpandableNavigation,
  ExpandableNavigationItem,
  ExpandableNavigationItemList,
  ExpandableNavigationItemButton,
  ExpandableNavigationItemText,
  ExpandableNavigationItemIcon,
} from 'app/src/components/ExpandableNavigation';
import { LDFlags, Tabs } from './constants';
import styles from './EnterpriseDashboardNavigation.less';

const format = forTemplate('enterprise_dashboard_sidebar');

interface HasRelatedEnterprisesNoticeProps {
  parentEnterprise?: {
    displayName: string;
  };
  relatedEnterprises?: {
    count: number;
    displayName: string;
  }[];
}

const HasRelatedEnterprisesNotice = ({
  parentEnterprise,
  relatedEnterprises,
}: HasRelatedEnterprisesNoticeProps) => {
  if (parentEnterprise) {
    return (
      <>
        {format('license-count-has-parent', {
          parentDisplayName: parentEnterprise.displayName,
        })}
      </>
    );
  } else if (relatedEnterprises && relatedEnterprises.length > 0) {
    return (
      <>
        {format('license-count-has-children')}
        <ul>
          {relatedEnterprises.map((e) => (
            <li>
              {format('related-enterprise-billable-members-count', {
                displayName: <strong>{e.displayName}</strong>,
                count: e.count,
              })}
            </li>
          ))}
        </ul>
      </>
    );
  }
  return null;
};

interface NearMaxMembersNoticeProps {
  enterpriseMaxMembers: number;
  totalMembers: number;
}
const NearMaxMembersNotice = ({
  enterpriseMaxMembers,
  totalMembers,
}: NearMaxMembersNoticeProps) => {
  const availableLicenses = enterpriseMaxMembers - totalMembers;
  if (availableLicenses === 1) {
    return <>{format('remaining-license-count-singular')}</>;
  } else {
    return (
      <>{format('remaining-license-count-plural', { availableLicenses })}</>
    );
  }
};

const AtMaxMembersNotice = () => (
  <>
    {format('no-seats-remaining', {
      sales: <a href="mailto:sales@trello.com">{format('sales')}</a>,
    })}
  </>
);

interface LicensesNoticeProps {
  licensesLoading: boolean;
  nearMaxMembers: boolean;
  atMaxMembers: boolean;
  enterpriseMaxMembers: number;
  totalMembers: number;
  parentEnterprise?: {
    displayName: string;
  };
  relatedEnterprises?: {
    count: number;
    displayName: string;
  }[];
  hasRelatedEnterprises: boolean;
}

const LicensesNotice = ({
  licensesLoading,
  nearMaxMembers,
  atMaxMembers,
  enterpriseMaxMembers,
  totalMembers,
  parentEnterprise,
  relatedEnterprises,
  hasRelatedEnterprises,
}: LicensesNoticeProps) => {
  if (!enterpriseMaxMembers) {
    return null;
  }
  let content = null;
  if (licensesLoading) {
    content = (
      <span title="Loading statistics...">
        <Spinner />
      </span>
    );
  } else {
    content = (
      <>
        {hasRelatedEnterprises && (
          <HasRelatedEnterprisesNotice
            parentEnterprise={parentEnterprise}
            relatedEnterprises={relatedEnterprises}
          />
        )}
        <div>{format('enterprise-licenses-used')} </div>
        <p>
          <strong>
            {format('licenses-count', {
              totalMembers,
              enterpriseMaxMembers,
            })}
          </strong>
        </p>
        {(atMaxMembers && <AtMaxMembersNotice />) ||
          (nearMaxMembers && (
            <NearMaxMembersNotice
              enterpriseMaxMembers={enterpriseMaxMembers}
              totalMembers={totalMembers}
            />
          ))}
      </>
    );
  }

  return (
    <div
      className={classNames(
        styles.enterpriseDashLicenses,
        !licensesLoading && styles.loaded,
        nearMaxMembers && styles.seatLimitNear,
        atMaxMembers && styles.seatLimitReached,
      )}
    >
      {content}
    </div>
  );
};

interface EnterpriseDashboardNavigationProps {
  idEnterprise: string;
  selectedTab: Tabs;
  onTabClick: (tab: Tabs) => void;
  teamsCount: number;
  pendingTeamsCount: number;
  enterpriseMembersCount: number;
  ssoUsersCount: number;
  outsideCollaboratorsCount: number;
  deactivatedCount: number;
  publicBoardsCount: number;
  powerUpsAdministrationEnabled: boolean;
  licensesLoading: boolean;
  nearMaxMembers: boolean;
  atMaxMembers: boolean;
  enterpriseMaxMembers: number;
  totalMembers: number;
  parentEnterprise?: {
    displayName: string;
  };
  relatedEnterprises?: {
    count: number;
    displayName: string;
  }[];
  hasRelatedEnterprises: boolean;
}

export const EnterpriseDashboardNavigation = ({
  idEnterprise,
  selectedTab,
  onTabClick,
  teamsCount,
  pendingTeamsCount,
  enterpriseMembersCount,
  ssoUsersCount,
  outsideCollaboratorsCount,
  deactivatedCount,
  publicBoardsCount,
  powerUpsAdministrationEnabled,
  ...rest
}: EnterpriseDashboardNavigationProps) => {
  const ssoSelfServeEnabled = useFeatureFlag(LDFlags.SSO_SELF_SERVE, false);

  return (
    <>
      <div className={styles.enterpriseDashSidebar}>
        <ExpandableNavigation>
          <div className={styles.header}>
            {format('enterprise-administration')}
          </div>
          <ExpandableNavigationItemList>
            <ExpandableNavigationItem>
              <ExpandableNavigationItemButton
                onClick={() => onTabClick(Tabs.MEMBERS)}
                selected={selectedTab === Tabs.MEMBERS}
              >
                <ExpandableNavigationItemIcon iconName={'member'} />
                <ExpandableNavigationItemText>
                  {format('billable-members-count', {
                    enterpriseMembersCount: String(enterpriseMembersCount),
                  })}
                </ExpandableNavigationItemText>
              </ExpandableNavigationItemButton>
            </ExpandableNavigationItem>
            <ExpandableNavigationItem>
              <ExpandableNavigationItemButton
                onClick={() => onTabClick(Tabs.MANAGED_MEMBERS)}
                selected={selectedTab === Tabs.MANAGED_MEMBERS}
                indented
                quiet
                small
              >
                <ExpandableNavigationItemIcon iconName={'member'} />
                <ExpandableNavigationItemText>
                  {format('managed-members-count', {
                    ssoUsersCount: String(ssoUsersCount),
                  })}
                </ExpandableNavigationItemText>
              </ExpandableNavigationItemButton>
            </ExpandableNavigationItem>
            <ExpandableNavigationItem>
              <ExpandableNavigationItemButton
                onClick={() => onTabClick(Tabs.BOARDGUESTS_MEMBERS)}
                selected={selectedTab === Tabs.BOARDGUESTS_MEMBERS}
                indented
                quiet
                small
              >
                <ExpandableNavigationItemIcon iconName={'member'} />
                <ExpandableNavigationItemText>
                  {format('boardguests-count', {
                    ssoUsersCount: String(ssoUsersCount),
                    outsideCollaboratorsCount: String(
                      outsideCollaboratorsCount,
                    ),
                  })}
                </ExpandableNavigationItemText>
              </ExpandableNavigationItemButton>
            </ExpandableNavigationItem>
            <ExpandableNavigationItem>
              <ExpandableNavigationItemButton
                onClick={() => onTabClick(Tabs.DEACTIVATED_MEMBERS)}
                selected={selectedTab === Tabs.DEACTIVATED_MEMBERS}
                indented
                quiet
                small
              >
                <ExpandableNavigationItemIcon iconName={'member'} />
                <ExpandableNavigationItemText>
                  {format('deactivated-members-count', {
                    deactivatedCount: String(deactivatedCount),
                  })}
                </ExpandableNavigationItemText>
              </ExpandableNavigationItemButton>
            </ExpandableNavigationItem>
            <ExpandableNavigationItem>
              <ExpandableNavigationItemButton
                onClick={() => onTabClick(Tabs.TEAMS)}
                selected={selectedTab === Tabs.TEAMS}
              >
                <ExpandableNavigationItemIcon iconName={'organization'} />
                <ExpandableNavigationItemText>
                  {format('teams', { teamsCount: String(teamsCount) })}
                </ExpandableNavigationItemText>
              </ExpandableNavigationItemButton>
            </ExpandableNavigationItem>
            <ExpandableNavigationItem>
              <ExpandableNavigationItemButton
                onClick={() => onTabClick(Tabs.PENDING_TEAMS)}
                selected={selectedTab === Tabs.PENDING_TEAMS}
                indented
                quiet
                small
              >
                <ExpandableNavigationItemIcon iconName={'organization'} />
                <ExpandableNavigationItemText>
                  {format('pending-requests-count', {
                    pendingRequestsCount: String(pendingTeamsCount),
                  })}
                </ExpandableNavigationItemText>
              </ExpandableNavigationItemButton>
            </ExpandableNavigationItem>
            <ExpandableNavigationItem>
              <ExpandableNavigationItemButton
                onClick={() => onTabClick(Tabs.TEAM_SETTINGS)}
                selected={selectedTab === Tabs.TEAM_SETTINGS}
              >
                <ExpandableNavigationItemIcon iconName={'private'} />
                <ExpandableNavigationItemText>
                  {format('security')}
                </ExpandableNavigationItemText>
              </ExpandableNavigationItemButton>
            </ExpandableNavigationItem>
            <ExpandableNavigationItem>
              <ExpandableNavigationItemButton
                onClick={() => onTabClick(Tabs.PUBLIC_BOARDS)}
                selected={selectedTab === Tabs.PUBLIC_BOARDS}
                indented
                quiet
                small
              >
                <ExpandableNavigationItemIcon iconName={'public'} />
                <ExpandableNavigationItemText>
                  {format('public-boards-count', {
                    publicBoardsCount: String(publicBoardsCount),
                  })}
                </ExpandableNavigationItemText>
              </ExpandableNavigationItemButton>
            </ExpandableNavigationItem>
            <ExpandableNavigationItem>
              <ExpandableNavigationItemButton
                onClick={() => {
                  if (powerUpsAdministrationEnabled) {
                    trackUe(
                      Noun.ENTERPRISE_DASHBOARD,
                      Verb.OPENS,
                      Noun.ENT_DASH_POWER_UPS_TAB,
                      '',
                      '',
                      'by clicking power-ups in the sidebar',
                      { idEnterprise },
                    );
                  } else {
                    trackUe(
                      Noun.ENTERPRISE_DASHBOARD,
                      Verb.OPENS,
                      Noun.ENT_DASH_POWER_UPS_TAB_INTRODUCTION_PAGE,
                      '',
                      '',
                      'by clicking power-ups in the sidebar',
                      { idEnterprise },
                    );
                  }
                  onTabClick(Tabs.POWER_UPS);
                }}
                selected={selectedTab === Tabs.POWER_UPS}
                indented
                quiet
                small
              >
                <ExpandableNavigationItemIcon iconName={'power-up'} />
                <ExpandableNavigationItemText>
                  {format('power-ups')}
                </ExpandableNavigationItemText>
              </ExpandableNavigationItemButton>
            </ExpandableNavigationItem>
            {ssoSelfServeEnabled && (
              <ExpandableNavigationItem>
                <ExpandableNavigationItemButton
                  onClick={() => {
                    trackUe(
                      Noun.ENTERPRISE_DASHBOARD,
                      Verb.OPENS,
                      Noun.ENT_DASH_SSO_SETTINGS_TAB,
                      '',
                      '',
                      'by clicking SSO settings in the sidebar',
                      { idEnterprise },
                    );
                    onTabClick(Tabs.SSO_SETTINGS);
                  }}
                  selected={selectedTab === Tabs.SSO_SETTINGS}
                  indented
                  quiet
                  small
                >
                  <ExpandableNavigationItemIcon iconName={'gear'} />
                  <ExpandableNavigationItemText>
                    {format('sso')}
                  </ExpandableNavigationItemText>
                </ExpandableNavigationItemButton>
              </ExpandableNavigationItem>
            )}
          </ExpandableNavigationItemList>
          <LicensesNotice {...rest} />
        </ExpandableNavigation>
      </div>
    </>
  );
};
